import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type { APIMission, Mission, MissionForm } from '../pages/missions/missions.types';
import type { Vehicle } from '../pages/fleet/fleet.types';
import type { MissionProgressDataRaw } from '../components/missionProgress/chargingMissionChart/chargingMissionChart.types';
import type {
    ChargingConnectionData,
    ChargingStationSelection,
} from '../features/missionProgress/chargingConnection/chargingConnection.types';
import { MISSION_STATUS_ORDER } from '../constants';

type GetMissionsResponse = {
    content: Mission[];
    totalPages: number;
    totalElements: number;
    numberOfElements: number;
    pageable: {
        pageNumber: number;
    };
};

export const missionApi = createApi({
    reducerPath: 'missionApi',
    baseQuery,
    tagTypes: ['Missions', 'Vehicles'],
    keepUnusedDataFor: 60,
    endpoints: (builder) => ({
        getPaginatedMission: builder.infiniteQuery<GetMissionsResponse, number, number>({
            query: ({ queryArg, pageParam }) => ({
                url: 'mission-service/missions',
                params: {
                    pageSize: queryArg,
                    pageNo: pageParam,
                },
            }),
            transformResponse: (response: GetMissionsResponse) => {
                return {
                    ...response,
                    content: response.content.map((mission) => ({
                        ...mission,
                        statusPriority: MISSION_STATUS_ORDER[mission.missionStatus] ?? null,
                    })),
                };
            },
            providesTags: (result) =>
                result
                    ? [
                          { type: 'Missions', id: 'LIST' },
                          ...result.pages.flatMap((page) =>
                              page.content.map((mission) => ({
                                  type: 'Missions' as const,
                                  id: mission.missionId,
                              }))
                          ),
                      ]
                    : [{ type: 'Missions', id: 'LIST' }],
            infiniteQueryOptions: {
                initialPageParam: 0,
                getNextPageParam: (lastPage, allPages, lastPageParam) => {
                    const currentPage = lastPage?.pageable?.pageNumber;
                    const totalPages = lastPage.totalPages;
                    if (currentPage + 1 >= totalPages) {
                        return undefined;
                    }
                    return lastPageParam + 1;
                },
            },
        }),
        getMissions: builder.query<Mission[], void>({
            query: () => 'mission-service/missions',

            transformResponse: (response: GetMissionsResponse): Mission[] => {
                return response.content.map((mission) => ({
                    ...mission,
                    statusPriority: MISSION_STATUS_ORDER[mission.missionStatus] ?? null,
                }));
            },
            providesTags: (result) =>
                result
                    ? [
                          { type: 'Missions', id: 'LIST' },
                          ...result.map((m) => ({ type: 'Missions' as const, id: m.missionId })),
                      ]
                    : [{ type: 'Missions', id: 'LIST' }],
        }),
        getVehicles: builder.query<Vehicle[], void>({
            query: () => 'mission-service/vehicle',
            providesTags: [{ type: 'Vehicles', id: 'LIST' }],
        }),
        createMission: builder.mutation<APIMission, MissionForm>({
            query: (body) => ({
                url: 'mission-service/missions',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Missions', id: 'LIST' }],
        }),
        editMission: builder.mutation<void, MissionForm & { missionId: number }>({
            query: ({ missionId, ...body }) => ({
                url: 'mission-service/missions',
                method: 'PUT',
                body: { missionId, ...body },
            }),
            invalidatesTags: [{ type: 'Missions', id: 'LIST' }],
        }),
        deleteMission: builder.mutation<void, number>({
            query: (missionId) => ({
                url: `mission-service/missions?missionId=${missionId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Missions', id: 'LIST' }],
        }),
        getMissionProgressInfo: builder.query<MissionProgressDataRaw, void>({
            query: () => 'mission-service/missions/info?mission=&startDate=&endDate=',
        }),
        getChargingConnectionData: builder.mutation<ChargingConnectionData, ChargingStationSelection>({
            query: (body) => ({
                url: 'mission-service/missions/power-chart',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useGetPaginatedMissionInfiniteQuery,
    useGetMissionsQuery,
    useGetVehiclesQuery,
    useCreateMissionMutation,
    useEditMissionMutation,
    useDeleteMissionMutation,
    useGetMissionProgressInfoQuery,
    useGetChargingConnectionDataMutation,
} = missionApi;
